<template>
  <aw-spring>
    <div>
      <lv-heading
        :id="Navigation[ID_BASKET_TOP]"
        tag="h1"
        level="2"
        version2
      >
        {{ $awt('aw.basket_page.heading') }}
      </lv-heading>
      <div v-if="numberOfSplits !== 1" :class="$style.splitNumberDescription">
        <p v-if="numberOfSplits > 1" v-text="$awt('aw.basket_page.more_than_one_split.text')" />
        <aw-empty v-else-if="numberOfSplits === 0">
          <span v-text="$awt('aw.basket_page.zero_split.text')" />
        </aw-empty>
      </div>
      <section v-if="numberOfSplits" :aria-labelledby="idOrderCartSummary" :class="$style.orderCartSummary">
        <h2 :id="idOrderCartSummary" class="awSrOnlyAbs" v-text="$awt('aw.basket_page.order_cart_summary.heading')" />
        <div :id="idOrderCartSummaryAnimation" :class="$style.accordionTransition">
          <aw-order-all-splits-summary heading-tag="h2" :class="$style.orderCartSummaryContent">
            <template #default="{ isBtnInNewline }">
              <div :class="$style.desktopVoucher">
                <aw-add-coupon-code-version2 :is-btn-in-newline="isBtnInNewline" />
              </div>
            </template>
          </aw-order-all-splits-summary>
        </div>
        <div :class="$style.expandBtnWrap">
          <aw-show-more-accordion v-model="accordionIsExpanded" v-bind="accordionConfig" />
        </div>
        <div :class="$style.mobileVoucher">
          <aw-add-coupon-code-version2 :is-btn-in-newline="false" />
        </div>
      </section>
      <h2 v-if="cartNotificationsLength" class="awSrOnlyAbs" v-text="$awt('aw.basket_page.coupons.heading')" />
      <div
        :class="[
          $style.couponInformationMessage,
          {
            [$style.couponInformationMessageEmpty]: !cartNotificationsLength,
          },
        ]"
      >
        <aw-notification
          live-region="assertive"
          :notifications="cartNotifications"
          :wrapper-classes="[$style.cartNotifications]"
          is-margin-less
        />
      </div>
      <div :class="$style.orderCartSplitTabs">
        <h2 :id="idCartTab" class="awSrOnlyAbs" v-text="$awt('aw.basket_page.basket_tab.heading')" />
        <aw-order-split-tab
          v-model:selected-tab="selectedTab"
          heading-tag="h2"
          :aria-labelledby="idCartTab"
        >
          <template #splitTop>
            <client-only v-if="boxCategoryProps">
              <aw-cart-boxes v-if="isBoxCategoryMobile" v-bind="boxCategoryProps" :class="$style.mobileBoxCategory" />
            </client-only>
          </template>
        </aw-order-split-tab>
      </div>
      <div v-if="boxCategoryProps" :class="$style.buyPaperBag">
        <client-only>
          <aw-cart-boxes v-if="!isBoxCategoryMobile" v-bind="boxCategoryProps" />
        </client-only>
      </div>
      <div v-if="cartItemSkusForRecommendations">
        <aw-sf-recommendation
          page="cart"
          :for-items="cartItemSkusForRecommendations"
          :analytics-banner-interaction="analyticsBannerInteraction"
          analytics-measurable
        />
      </div>
    </div>
    <client-only>
      <aw-spinner
        v-if="isSpinnerVisible"
        :class="$style.basketSpinner"
        width-with-unit="6rem"
        height-with-unit="6rem"
        border-width-with-unit="0.4em"
      />
    </client-only>
  </aw-spring>
</template>

<script setup lang="ts">
  import { computed, defineAsyncComponent, h, onBeforeUnmount, onBeforeUpdate, onMounted, provide, ref, useCssModule, watch } from 'vue';
  import { useAsyncData, useId, useNuxtApp, useRoute } from 'nuxt/app';
  import { PUSH_VIEW_CART } from '~~/common/plugins/aw-analytics';
  import { ID_BASKET_TOP, Navigation } from '~~/common/config/Navigation';
  import { useOrderStore } from '~~/common/stores/order';
  import { useLoyaltyCardStore } from '~~/shop/app/stores/loyaltyCard';
  import { useUserInterfaceStore } from '~~/common/stores/userInterface';
  import { useModalStore } from '~~/common/stores/modal';
  import { useRedirectOnLoginTypeChange } from '~~/common/composables/useRedirectOnLoginTypeChange';
  import { useOrderSplit } from '~~/common/composables/useOrderSplit';
  import { LvHeading } from '~~/common/components/loginet-vue-shop/index';
  import AwSpinner from '~~/common/components/Common/AwSpinner.vue';
  import AwSpring from '~~/common/components/AwSpring.vue';
  import AwOrderAllSplitsSummary from '~~/shop/app/components/AwOrderAllSplitsSummary.vue';
  import AwOrderSplitTab from '~~/shop/app/components/AwOrderSplitTab.vue';
  import AwAddCouponCodeVersion2 from '~~/shop/app/components/AwAddCouponCodeVersion2.vue';
  import AwShowMoreAccordion from '~~/common/components/Common/AwShowMoreAccordion.vue';
  import { CT_UNSPLITTED } from '~~/common/config/CheckoutTypes';
  import { defineI18nRoute } from '#i18n';
  import { AnalyticsBannerInteraction } from '~~/common/utils/AnalyticsBannerInteraction';
  import { calculatePromotionCompanyNo, calculatePromotionPageHref } from '~~/common/utils/analyticsHelpers';
  import { asBooleanCookie } from '~~/common/utils/cookie';
  import type { CartSplit, CartVoucher } from '~~/common/stores/order';
  import { defineLazyHydrationComponent } from '#app/composables/lazy-hydration';

  const AwSfRecommendation = defineLazyHydrationComponent('visible', () => import('~~/common/components/Common/AwSfRecommendation.vue'));

  /** A voucher paired with the split it came from, so the notification can name the splits it missed. */
  type SplitVoucher = {
    checkoutType?: string;
    voucher: CartVoucher;
  };
  /** One entry of the `notifications` map `AwNotification` renders. */
  type CartNotification = NonNullable<
    | ReturnType<typeof createVoucherNotification>
    | ReturnType<typeof createCreditNotification>
  >;

  const AwNotification = defineAsyncComponent(() => import('~~/common/components/Common/AwNotification.vue'));
  const AwNotificationItemCredit = defineAsyncComponent(() => import('~~/common/components/AwNotificationItemCredit.vue'));
  const AwNotificationItemSplitted = defineAsyncComponent(() => import('~~/common/components/Common/AwNotificationItemSplitted.vue'));
  const AwCartBoxes = defineAsyncComponent(() => import('~~/shop/app/components/AwCartBoxes.vue'));
  const AwEmpty = defineAsyncComponent(() => import('~~/common/components/Common/AwEmpty.vue'));

  defineOptions({
    name: 'BasketPage',
  });
  // eslint-disable-next-line no-undef
  definePageMeta({
    layout: 'full-width',
  });

  false && /*#__PURE__*/ defineI18nRoute({
    paths: {
      en: '/shop/basket',
      hu: '/shop/kosar',
    },
  });

  const {
    $awt,
    $awAnalytics,
    $cookies,
    $logger,
    $router,
    $pathAutofocusBlock,
    $getHref,
    $getHost,
  } = useNuxtApp();

  const {
    hash,
  } = useRoute();
  const $style = useCssModule();

  provide('isCartPage', true);

  useRedirectOnLoginTypeChange();

  const {
    fetchOrder,
    removeCartItem,
    getAllUncreditableItem,
    getCurrentFlattenedOrderItems,
  } = useOrderStore();
  const {
    addQueueSafeConfirmModal,
    removeQueueSafeConfirmModal,
  } = useModalStore();

  const idOrderCartSummary = useId();
  const idOrderCartSummaryAnimation = useId();

  const idCartTab = useId();

  const { splits } = useOrderSplit();

  const initialCheckoutType = splits.value.find(s => s?.checkoutType === useRoute()?.query?.split)?.checkoutType || null;

  const selectedTab = ref(initialCheckoutType ? `aw_${initialCheckoutType}` : null);

  const accordionIsExpanded = ref(false);

  /** Tab ids whose selection restarts the `view_cart` analytics timeout. */
  const ANALYTICS_TABS: (string | null)[] = ['aw_FOOD', 'aw_NONFOOD'];

  const CREDIT_COOKIE_NAME = '1d_cart_credit_reminder';
  const initialCreditSelection = ref(asBooleanCookie($cookies.get(CREDIT_COOKIE_NAME)));

  const uncreditableItems = ref<NonNullable<Awaited<ReturnType<typeof getAllUncreditableItem>>>>([]);
  const selectedCredit = ref(-1);

  const analyticsBannerInteraction = AnalyticsBannerInteraction.create({
    promotionComponentId: 'reco_list',
    promotionIndex: 1,
    promotionBannerIndex: 1,
    promotionCompanyNo: calculatePromotionCompanyNo() ?? null,
    promotionPageHref: calculatePromotionPageHref($getHref(), $getHost() ?? '') ?? '',
    promotionName: null,
  });


  const numberOfSplits = computed(() => {
    return splits.value.length || 0;
  });

  const numberOfCreditableSplits = computed(() => {
    return splits.value.filter(isSplitCreditable).length;
  });

  const mediaQueries = useUserInterfaceStore().useMediaQueries();

  const isBoxCategoryMobile = computed(() => mediaQueries.value['mobile-max']);

  const boxCategoryId = computed(() => {
    return splits.value.find(s => s.boxOption)?.boxCategoryId || null;
  });

  const boxCategoryProps = computed(() => {
    const id = boxCategoryId.value;
    return id === null ? null : {
      boxCategoryId: id,
      compact: isBoxCategoryMobile.value,
    };
  });

  const cartNotifications = computed(() => {
    const allVouchers = splits.value.reduce<SplitVoucher[]>((acc, {
      checkoutType,
      vouchers,
    }) => {
      return [
        ...acc,
        ...(vouchers || []).map(voucher => ({
          checkoutType,
          voucher,
        })),
      ];
    }, []);
    const notAppliedVouchers = (allVouchers
      .filter(({ voucher }) => {
        return !voucher.applied;
      })
      .reduce<Record<string, (string | undefined)[]>>((acc, {
        checkoutType,
        voucher,
      }) => {
        const objectId = voucher.objectId ?? '';
        if (!acc[objectId]) {
          acc[objectId] = [];
        }
        acc[objectId].push(checkoutType);
        return acc;
      }, {})
    );
    const voucherNotis = allVouchers.reduce<Record<string, CartNotification>>((acc, { voucher }) => {
      const notAppliedSplits = notAppliedVouchers[voucher.objectId ?? ''] || [];
      const vn = createVoucherNotification({
        voucher,
        notAppliedSplits,
      });
      if (vn) {
        acc[vn.uuid] = vn;
      }
      return acc;
    }, {});
    const loanNotis = splits.value.reduce<Record<string, CartNotification>>((acc, split) => {
      const cr = createCreditNotification({ split });
      if (cr) {
        acc[cr.uuid] = cr;
      }
      return acc;
    }, {});
    return { ...voucherNotis, ...loanNotis };
  });

  const cartNotificationsLength = computed(() => {
    return Object.keys(cartNotifications.value).length;
  });

  const isSpinnerVisible = computed(() => {
    return Boolean(useUserInterfaceStore().basketOperationLoading);
  });

  const accordionConfig = computed(() => ({
    referenceId: idOrderCartSummaryAnimation,
    closedHeight: $style.accordionMobileHeight,
    'aria-label': $awt(accordionIsExpanded.value
      ? 'aw.basket_page.order_cart_summary.shrink.label'
      : 'aw.basket_page.order_cart_summary.expand.label',
    ),
    text: $awt(accordionIsExpanded.value
      ? 'aw.basket_page.order_cart_summary.shrink.text'
      : 'aw.basket_page.order_cart_summary.expand.text',
    ),
  }));

  const cartWatcher = computed(() => `cart_${hash}`);

  const cartItemSkusForRecommendations = computed(() => {
    return getCurrentFlattenedOrderItems.map((item) => {
      return item.variant?.sku;
    }).join(',');
  });

  provide('uncreditableItems', uncreditableItems);

  useAsyncData(
    cartWatcher.value,
    async () => {
      try {
        if (numberOfSplits.value === 0 || (numberOfSplits.value === 1 && splits.value[0]?.checkoutType === CT_UNSPLITTED)) {
          await fetchOrder({
            id: 'current',
            fetchNonfoodCheckoutIfNFCartNotEmpty: false,
            canStartSecondAction: true,
            canRunSimultaneous: true,
          });
        }
      } catch (err) {
        $logger.error(err);
      }
      return {};
    },
    {
      watch: [
        cartWatcher,
      ],
    });

  onMounted(() => {
    watch(
      computed(() => useLoyaltyCardStore().expectedLoyaltyPointsKey),
      () => {
        useLoyaltyCardStore().fetchExpectedLoyaltyPoints({
          id: 'current',
          type: 'ALL',
        });
      },
      {
        immediate: true,
      },
    );

    watch(
      computed(() => Boolean(numberOfCreditableSplits.value)),
      (newVal) => {
        if (newVal) {
          // NOTE: The setTimeout is added around the modal to fix a
          // bodyLock issue. It happened when the user navigated from
          // somewhere to the basket and the cart is unsplitted. sometimes
          // (randomly it happened, but there is hack to reproduce it every
          // time): 1. put items into the cart which would trigger the
          // popup. 2. be on product list 3. go to cart from the minicart 4.
          // wait for the popup 5. use the browser back button 6. close the
          // popup on the productlist 7. erase the cookie 8. use the forward
          // button to go to the cart 9. body is locked
          setTimeout(() => {
            openCreditModal();
          }, 2000);
        }
      },
      {
        immediate: true,
      },
    );
    watch(
      selectedTab,
      (newVal) => {
        const checkoutType = newVal?.slice('aw_'.length);
        const params = new URLSearchParams(document.location.search);
        if (params.get('split') !== checkoutType) {
          const currentRoute = $router.currentRoute.value;
          $pathAutofocusBlock.add(true);
          $router.replace({
            query: {
              ...currentRoute.query,
              ...(checkoutType ? { split: checkoutType } : null),
            },
            hash: currentRoute.hash,
          });

        }
        if (ANALYTICS_TABS.includes(newVal) || !sendAnalyticsTimeout.value) {
          sendStartAnalyticsTimeout();
        }
      },
      {
        immediate: true,
      },
    );
  });

  onBeforeUpdate(() => {
    sendStartAnalyticsTimeout();
  });

  onBeforeUnmount(() => {
    clearTimeout(sendAnalyticsTimeout.value ?? undefined);
  });

  function sendStartAnalyticsTimeout () {
    if (import.meta.server || sendAnalyticsTimeout.value) {
      return;
    }
    sendAnalyticsTimeout.value = setTimeout(() => {
      $awAnalytics[PUSH_VIEW_CART]({
        splits: splits.value,
      });
    }, 100);
  }

  watch(() => useOrderStore().order, async () => {
    splits.value.forEach((split) => {
      if (selectedCredit.value !== -1 && !isSplitCreditable(split)) {
        selectedCredit.value = -1;
        uncreditableItems.value = [];
      }
    });
  });

  function isSplitCreditable (split: CartSplit) {
    return split?.mixedCreditable && split?.infos?.creditableButtons?.length;
  }

  function createCreditNotification ({ split }: { split: CartSplit }) {
    if (isSplitCreditable(split)) {
      return {
        uuid: `loan_${split.checkoutType}`,
        is: {
          render: () => h(
            AwNotificationItemCredit,
            {
              creditableButtons: split?.infos?.creditableButtons || [],
              initialCreditSelection: initialCreditSelection.value,
              async onChangedBaremId (baremId: number) {
                if (baremId && baremId !== -1) {
                  selectedCredit.value = baremId;
                  // `getAllUncreditableItem` swallows its error and returns undefined; the consumer
                  // (`AwOrderSplitCart`) calls `.includes()` on the injected value, so keep it an array.
                  uncreditableItems.value = await getAllUncreditableItem(baremId) || [];
                } else {
                  selectedCredit.value = -1;
                  uncreditableItems.value = [];
                }
              },
            },
          ),
        },
        type: 'info',
        manualClose: false,
        text: {
          subtitle: split.infos?.containsMixedCreditable || '',
        },
      };
    }
  }

  function createVoucherNotification (
    {
      voucher,
      notAppliedSplits,
    }: { voucher: CartVoucher, notAppliedSplits: (string | undefined)[] },
  ) {
    // Bound out so the delete button's handler sees the narrowed, defined uuid.
    const voucherUuid = voucher.uuid;
    const TOKEN_GROUP = notAppliedSplits.length ? 'coupon_not_applied' : 'coupon_applied';
    const notAppliedSplitsText = ((notAppliedSplits.length && notAppliedSplits.length < numberOfSplits.value)
      ? (notAppliedSplits
        .map((checkoutType) => {
          return $awt(`aw.basket_page.coupons.not_applied_split.${checkoutType}`.toLowerCase());
        })
        .join(' ')
      )
      : ''
    );
    const tokenParams = {
      name: voucher.name || '',
      description: voucher.description || '',
      tooltip: voucher.tooltip || '',
      notAppliedSplitsText,
    };
    return voucher ? {
      uuid: `voucher_${voucher.objectId}`,
      is: AwNotificationItemSplitted,
      type: 'info',
      manualClose: false,
      text: {
        title: $awt(
          `aw.basket_page.coupons.${TOKEN_GROUP}.name`,
          tokenParams,
        ),
        subtitle: $awt(
          `aw.basket_page.coupons.${TOKEN_GROUP}.description`,
          tokenParams,
        ),
        tooltip: $awt(
          `aw.basket_page.coupons.${TOKEN_GROUP}.tooltip`,
          tokenParams,
        ),
      },
      ...(!voucher.automatic && voucherUuid && {
        button: {
          is: 'button',
          html: $awt('aw.basket_page.coupons.coupon_delete_btn.text'),
          attrs: {
            class: [
              'buttonReset',
              $style.couponLink,
            ],
            type: 'button',
            onClick () {
              removeCartItem({
                uuid: voucherUuid,
                fromBasket: true,
              });
            },
          },
        },
      }),
    } : null;
  }

  const isCreditModalOpened = ref(false);
  const sendAnalyticsTimeout = ref<ReturnType<typeof setTimeout> | null>(null);

  function openCreditModal () {
    if (isCreditModalOpened.value) {
      return;
    }
    isCreditModalOpened.value = true;

    if (typeof initialCreditSelection.value !== 'boolean') {
      const uuid = 'cartCreditCookieModal';
      const onModalReaction = ({ isAccepted }: { isAccepted: boolean }) => {
        $cookies.set(
          CREDIT_COOKIE_NAME,
          isAccepted,
          {
            maxAge: 24 * 60 * 60,
            path: '/',
          },
        );
        removeQueueSafeConfirmModal({ uuid });
        isCreditModalOpened.value = false;
        initialCreditSelection.value = isAccepted;
      };
      addQueueSafeConfirmModal({
        uuid,
        data: {
          closable: true,
          data: {
            confirmButtonsDirection: 'horizontal',
            confirmModalSize: 'md',
            confirmTitle: $awt('aw.cart_credit_reminder.heading'),
            onClickX () {
              onModalReaction({ isAccepted: false });
            },
            buttons: {
              ok: {
                text: $awt('aw.cart_credit_reminder.accept'),
                attrs: {
                  onClick () {
                    onModalReaction({ isAccepted: true });
                  },
                },
              },
              cancel: {
                text: $awt('aw.cart_credit_reminder.reject'),
                attrs: {
                  onClick () {
                    onModalReaction({ isAccepted: false });
                  },
                },
              },
            },
            confirmText: `<div>${$awt('aw.cart_credit_reminder.description.broad_description')}</div><strong class="${$style.creditCtaDescription}">${$awt('aw.cart_credit_reminder.description.cta_description')}</strong>`,
          },
        },
      });
    }
  }
</script>

<style module lang="scss" rel="stylesheet/scss">

.basketSpinner {
  position: fixed;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100vw;
  height: 100%;
  margin: auto;
  background: rgba($color-seashell, 0.8);
}

.splitNumberDescription {
  @include font(500, 14px, 20px);
  padding-top: 12px;

  @include tablet(min) {
    @include font(500, 16px, 24px);
    padding-top: 20px;
  }
}

.orderCartSummary,
.couponInformationMessage:not(.couponInformationMessageEmpty),
.orderCartSplitTabs,
.mobileVoucher,
.expandBtnWrap {
  padding-top: 20px;
  transition: padding $animation-speed-medium $animation-timing-function;

  @include tablet(min) {
    padding-top: 40px;
  }
}

.orderCartSummaryContent {
  @include mobile(max) {
    margin-bottom: 20px;
  }
}

.orderCartSplitTabs {
  padding-bottom: 20px;

  @include tablet(min) {
    padding-bottom: 40px;
  }
}

.buyPaperBag {
  padding: 40px 0 60px;

  @include mobile(max) {
    display: none;
  }
}

.mobileBoxCategory {
  margin-bottom: 12px;
}

.cartNotifications {
  display: flex;
  flex-direction: column;
  gap: 8px;

  @include tablet(min) {
    gap: 20px;
  }
}

.creditCtaDescription {
  @include mobile(max) {
    font-weight: 500 !important;
  }
}

.couponLink {
  font-weight: 700;
  margin-right: auto;
  text-decoration: underline;

  &:focus-visible {
    outline: 1px solid currentColor;
  }
}

.mobileVoucher {
  @include tablet(min) {
    display: none;
  }
}

.desktopVoucher {
  width: 100%;
  margin-top: 8px;

  @include mobile(max) {
    display: none;
  }
}

.expandBtnWrap {
  display: flex;
  justify-content: center;
  border-top: 1px dashed $color-dash-separator;

  @include tablet(min) {
    display: none;
  }
}

$accordionMobileHeight: 128px;

.accordionTransition {

  @include tablet(min) {
    height: initial !important;
  }

  @include mobile(max) {
    overflow: hidden;
    height: $accordionMobileHeight;
  }
}

:export {
  accordionMobileHeight: $accordionMobileHeight;
}
</style>
